import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { errorMessage, get, post, type LedgerEntry, type Pricing } from "../../lib/api";
import { centsToUsd, creditsToUsd, formatDate, formatNumber } from "../../lib/format";
import { usePublicConfig } from "../../lib/session";
import { Alert, Badge, Button, Card, ErrorBox, Loading, PageHeader, Stat, Table, Td } from "../../components/ui";
import { can, useWorkspace, useWorkspaceId } from "./hooks";

interface Billing {
  balance: number;
  ledger: LedgerEntry[];
  packs: Array<{ id: string; name: string; max_records: number; status: string; migration_id: string | null; purchased_at: number }>;
  managedByPartner: boolean;
}

export function usePricing() {
  return useQuery({ queryKey: ["pricing"], queryFn: () => get<Pricing>("/api/billing/pricing"), staleTime: 60_000 });
}

export function BillingPage() {
  const ws = useWorkspaceId();
  const qc = useQueryClient();
  const detail = useWorkspace(ws);
  const cfg = usePublicConfig();
  const { checkout } = useSearch({ strict: false }) as { checkout?: string };
  const billing = useQuery({ queryKey: ["billing", ws], queryFn: () => get<Billing>(`/api/billing/workspaces/${ws}`) });
  const pricing = usePricing();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const manage = can(detail.data?.permissions, "billing.manage");

  if (billing.isLoading) return <Loading />;
  if (billing.error) return <ErrorBox error={billing.error} />;
  const b = billing.data!;

  const buy = async (kind: "credits" | "pack", itemId: string) => {
    setBusy(itemId);
    setError(null);
    try {
      const r = await post<{ url: string }>(`/api/billing/workspaces/${ws}/checkout`, { kind, itemId });
      window.location.assign(r.url);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(null);
    }
  };

  const devGrant = async () => {
    await post("/api/dev/billing/credits", { walletId: `ws:${ws}`, credits: 10_000 });
    qc.invalidateQueries({ queryKey: ["billing", ws] });
    qc.invalidateQueries({ queryKey: ["ws", ws] });
  };

  return (
    <>
      <PageHeader title="Credits & billing" subtitle="Prepaid credits. 1 credit = US$0.01. Previews and reverts are free." />
      {checkout === "success" && <Alert tone="ok">Payment received. Credits appear as soon as Stripe confirms it, usually within a few seconds.</Alert>}
      {checkout === "cancelled" && <Alert>Checkout was cancelled. Nothing was charged.</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      {b.managedByPartner && <Alert tone="accent">This workspace is managed by a partner, who can allocate credits to it.</Alert>}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Balance" value={formatNumber(b.balance)} hint={creditsToUsd(b.balance)} />
        <Stat label="Migration packs available" value={b.packs.filter((p) => p.status === "available").length} />
        <Stat label="Packs in use" value={b.packs.filter((p) => p.status === "redeemed").length} />
      </div>

      {manage && pricing.data && (
        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <Card title="Buy credits" actions={(cfg.data?.environment === "dev" || cfg.data?.environment === "test") && <Button size="sm" variant="secondary" onClick={devGrant}>Add 10,000 test credits</Button>}>
            {pricing.data.placeholder && <p className="muted mb-3 text-xs">Prices are placeholders while pricing is finalised.</p>}
            <div className="space-y-2">
              {pricing.data.bundles.map((bd) => (
                <div key={bd.id} className="border-default flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <div className="font-medium">{formatNumber(bd.credits)} credits</div>
                    <div className="muted text-xs">{centsToUsd(bd.priceCents)}</div>
                  </div>
                  <Button size="sm" loading={busy === bd.id} onClick={() => buy("credits", bd.id)}>
                    Buy
                  </Button>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Migration packs">
            <p className="muted mb-3 text-sm">One fixed price covers every run of one migration up to its size: dry runs, the run and reverts.</p>
            <div className="space-y-2">
              {pricing.data.packs.map((p) => (
                <div key={p.id} className="border-default flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="muted text-xs">{centsToUsd(p.priceCents)}</div>
                  </div>
                  <Button size="sm" variant="secondary" loading={busy === p.id} onClick={() => buy("pack", p.id)}>
                    Buy
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {b.packs.length > 0 && (
        <Card title="Your packs" className="mb-6">
          <Table head={["Pack", "Up to", "Status", "Purchased"]}>
            {b.packs.map((p) => (
              <tr key={p.id}>
                <Td>{p.name}</Td>
                <Td className="tabular">{formatNumber(p.max_records)} records</Td>
                <Td>
                  <Badge tone={p.status === "available" ? "ok" : "neutral"}>{p.status === "available" ? "available" : "in use"}</Badge>
                </Td>
                <Td className="muted">{formatDate(p.purchased_at)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      <Card title="Ledger">
        <Table head={["When", "Type", "Description", "Amount", "Balance"]}>
          {b.ledger.map((l) => (
            <tr key={l.id}>
              <Td className="muted whitespace-nowrap">{formatDate(l.at)}</Td>
              <Td>
                <Badge>{l.type.replace(/_/g, " ")}</Badge>
              </Td>
              <Td>{l.description}</Td>
              <Td className={`tabular text-right ${l.amount < 0 ? "text-danger" : l.amount > 0 ? "text-ok" : ""}`}>
                {l.amount > 0 ? "+" : ""}
                {formatNumber(l.amount)}
              </Td>
              <Td className="tabular text-right">{formatNumber(l.balance_after)}</Td>
            </tr>
          ))}
        </Table>
        {!b.ledger.length && <p className="muted p-3 text-sm">No transactions yet.</p>}
      </Card>
    </>
  );
}
